import React, { useState, useEffect } from "react";
import { Line, Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from "chart.js";
import axios from "axios";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import InternalHeader from "../InternalHeader";
import Sidebar from "../Sidebar";
import "./Dashboard.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [appointmentData, setAppointmentData] = useState(null);
    const [treatmentData, setTreatmentData] = useState(null);
    const [loadingAppointments, setLoadingAppointments] = useState(true);
    const [loadingTreatments, setLoadingTreatments] = useState(true);
    const [errorAppointments, setErrorAppointments] = useState(null);
    const [errorTreatments, setErrorTreatments] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentYearAppointments, setCurrentYearAppointments] = useState(new Date().getFullYear());
    const [genderData, setGenderData] = useState({ male: 0, female: 0 });
    const [loadingGender, setLoadingGender] = useState(true);
    const [errorGender, setErrorGender] = useState(null);

    const toggleSidebar = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    // Fetch appointment data based on year
    useEffect(() => {
        const fetchAppointments = async () => {
            setLoadingAppointments(true);
            setErrorAppointments(null);
            try {
                const response = await axios.post("https://api.hgcdental.clinic/monthly-appointments", { year: currentYearAppointments });
                const { labels, data } = response.data;

                setAppointmentData({
                    labels: labels,
                    datasets: [
                        {
                            label: `Appointments in ${currentYearAppointments}`,
                            data: data,
                            fill: false,
                            backgroundColor: "rgba(75,192,192,1)",
                            borderColor: "rgba(75,192,192,1)",
                        },
                    ],
                });
            } catch (err) {
                console.error("Error fetching appointment data:", err);
                setErrorAppointments("Failed to load appointment data.");
            } finally {
                setLoadingAppointments(false);
            }
        };

        fetchAppointments();
    }, [currentYearAppointments]);

    // Fetch treatment data based on month and year
    useEffect(() => {
        const fetchTreatments = async () => {
            setLoadingTreatments(true);
            setErrorTreatments(null);
            try {
                const response = await axios.get(`https://api.hgcdental.clinic/popular-treatments?month=${currentMonth}&year=${currentYear}`);
                const { labels, data } = response.data;

                setTreatmentData({
                    labels: labels,
                    datasets: [
                        {
                            label: "Popular Treatments",
                            data: data,
                            backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF"],
                        },
                    ],
                });
            } catch (err) {
                console.error("Error fetching treatment data:", err);
                setErrorTreatments("Failed to load treatment data.");
            } finally {
                setLoadingTreatments(false);
            }
        };

        fetchTreatments();
    }, [currentMonth, currentYear]);

    // Handle prev/next year for appointments
    const handlePrevYearAppointments = () => {
        setCurrentYearAppointments((prevYear) => prevYear - 1);
    };

    const handleNextYearAppointments = () => {
        setCurrentYearAppointments((prevYear) => prevYear + 1);
    };

    // Handle prev/next month for treatments
    const handlePrevMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear((prevYear) => prevYear - 1);
        } else {
            setCurrentMonth((prevMonth) => prevMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear((prevYear) => prevYear + 1);
        } else {
            setCurrentMonth((prevMonth) => prevMonth + 1);
        }
    };

    useEffect(() => {
        const fetchGenderStats = async () => {
            setLoadingGender(true);
            setErrorGender(null);
            try {
                const response = await axios.get("https://api.hgcdental.clinic/api/gender-patients", {
                    headers: { 
                        "x-api-key": process.env.REACT_APP_API_KEY // Secure API Key
                    },
                    withCredentials: true, // Include credentials if needed
                });
    
                setGenderData(response.data);
            } catch (err) {
                console.error("Error fetching gender data:", err);
                setErrorGender("Failed to load gender data.");
            } finally {
                setLoadingGender(false);
            }
        };
    
        fetchGenderStats();
    }, []);    

    // Sample data for Bar chart
    const barChartData = {
        labels: ["Male", "Female"],
        datasets: [
            {
                label: "Patient Gender",
                data: [genderData.male, genderData.female],  // Dynamically update values
                backgroundColor: ["#FF6384", "#36A2EB"],
            },
        ],
    };
    

    // // Sample data for Line chart
    // const lineChartData1 = {
    //     labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    //     datasets: [
    //         {
    //             label: 'Monthly Income',
    //             data: [85, 78, 80, 90, 60, 95],
    //             fill: false,
    //             backgroundColor: 'rgba(75,192,192,1)',
    //             borderColor: 'rgba(75,192,192,1)',
    //         },
    //     ],
    // };

    const bgImage = '/pictures/HGC-bg.jpg';

    return (
        <div className={`dashboard-container ${isSidebarVisible ? "sidebar-visible" : "sidebar-hidden"}`}>
            <div className="background-image" style={{ backgroundImage: `url(${bgImage})` }}> </div>
            <InternalHeader moduleName="Dashboard" toggleSidebar={toggleSidebar} />
            <div className="dashboard-content">
                <Sidebar isSidebarVisible={isSidebarVisible} />
                <div className="main-content">
                    {/* Appointments Over Time - Yearly */}
                    <div className="kanban-grid">
                        <div className="kanban-card">
                            <div className="chart-header">
                                <FaChevronLeft className="arrow-icon" onClick={handlePrevYearAppointments} />
                                <h3 className="chart-title">
                                    Appointments Over Time ({currentYearAppointments})
                                </h3>
                                <FaChevronRight className="arrow-icon" onClick={handleNextYearAppointments} />
                            </div>
                            {loadingAppointments ? (
                                <p>Loading...</p>
                            ) : errorAppointments ? (
                                <p className="error-message">{errorAppointments}</p>
                            ) : (
                                <Line data={appointmentData} options={{ responsive: true }} />
                            )}
                        </div>
                        <div className="kanban-card">
                            <h3>Gender of Patients</h3>
                            {loadingGender ? (
                                <p>Loading...</p>
                            ) : errorGender ? (
                                <p className="error-message">{errorGender}</p>
                            ) : (
                                <Bar data={barChartData} options={{ responsive: true }} />
                            )}
                        </div>
                    </div>

                    {/* Other Cards */}
                    <div className="kanban-grid">
                        {/* // <div className="kanban-card">
                        //     <h3>Income</h3>
                        //     <Line data={lineChartData1} options={{ responsive: true }} />
                        // </div> */}
                        <div className="kanban-card">
                            <div className="chart-header">
                                <FaChevronLeft className="arrow-icon" onClick={handlePrevMonth} />
                                <h3 className="chart-title">
                                    Popular Treatments ({new Date(currentYear, currentMonth - 1).toLocaleString("en-US", { month: "long", year: "numeric" })})
                                </h3>
                                <FaChevronRight className="arrow-icon" onClick={handleNextMonth} />
                            </div>
                            {loadingTreatments ? (
                                <p>Loading...</p>
                            ) : errorTreatments ? (
                                <p className="error-message">{errorTreatments}</p>
                            ) : (
                                <Bar data={treatmentData} options={{ responsive: true }} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
//thedrama
